import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Wine } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { MathCaptcha, useCaptcha } from "@/components/MathCaptcha";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/catalog";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const captcha = useCaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please fill in all fields"); return; }
    if (!captcha.answer) { toast.error("Please answer the security question"); return; }
    setLoading(true);
    try {
      await login(email, password, captcha.payload);
      toast.success("Welcome back");
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
      captcha.refresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-sm px-6">
        <div className="text-center mb-10">
          <a href="https://alfredwine.com/">
            <img src="https://customer-assets.emergentagent.com/job_vineyard-price-scout/artifacts/lksk5cgy_Albert%20%281%29.png" alt="AlfredWine.com" className="h-8 object-contain mx-auto mb-6" />
          </a>
          <h1 className="font-display text-2xl text-white/90 mb-1">{t.welcomeBack}</h1>
          <p className="font-body text-sm text-white/40">{t.signInToAccount}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="font-body text-xs text-white/40 tracking-wider uppercase block mb-2">{t.email}</label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-white/10 text-white/90 font-body text-sm placeholder:text-white/20 h-11 rounded-sm focus:border-merlot/50"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-body text-xs text-white/40 tracking-wider uppercase">{t.password}</label>
              <Link to="/forgot-password" className="font-body text-xs text-merlot-light hover:text-gold transition-colors" data-testid="goto-forgot-password">Forgot password?</Link>
            </div>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-transparent border-white/10 text-white/90 font-body text-sm placeholder:text-white/20 h-11 rounded-sm focus:border-merlot/50"
              placeholder="Enter your password"
            />
          </div>
          <MathCaptcha captcha={captcha} testPrefix="login-captcha" />
          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-merlot hover:bg-merlot-light text-white font-body text-sm h-11 rounded-sm shadow-[0_0_15px_rgba(158,42,43,0.3)] transition-all duration-300"
          >
            {loading ? t.signingIn : t.signIn}
          </Button>
        </form>
        <p className="font-body text-sm text-white/30 text-center mt-8">
          {t.dontHaveAccount}{" "}
          <Link to="/register" className="text-merlot-light hover:text-gold transition-colors" data-testid="goto-register">{t.createOne}</Link>
        </p>
      </div>
    </main>
  );
}
